import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatDate(dateString) {
  if (!dateString) return '';
  const str = String(dateString);
  let date;
  // YYYY-MM-DD format — parse as local date (not UTC)
  if (str.length === 10 && str.includes('-') && !str.includes('T')) {
    const [y, m, d] = str.split('-').map(Number);
    date = new Date(y, m - 1, d);
  } else {
    date = new Date(str);
  }
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

// Format form date field values — handles both new YYYY-MM-DD and old ISO formats
// Old ISO format like "2026-03-19T21:00:00.000Z" was actually March 20 in local time
export function formatFormDate(dateString) {
  if (!dateString) return '';
  const str = String(dateString);
  let date;
  // YYYY-MM-DD format — parse as local date (not UTC)
  if (str.length === 10 && str.includes('-') && !str.includes('T')) {
    const [y, m, d] = str.split('-').map(Number);
    date = new Date(y, m - 1, d);
  } else if (str.includes('T')) {
    // Old ISO format — extract correct date considering timezone shift
    const dt = new Date(str);
    // If UTC hour >= 12, it was likely the next day in user's local timezone
    if (dt.getUTCHours() >= 12) {
      const nextDay = new Date(dt);
      nextDay.setUTCDate(nextDay.getUTCDate() + 1);
      date = new Date(nextDay.getUTCFullYear(), nextDay.getUTCMonth(), nextDay.getUTCDate());
    } else {
      date = new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
    }
  } else {
    date = new Date(str);
  }
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatDateTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const CURRENCY_CONFIG = {
  RUB: { locale: 'ru-RU', currency: 'RUB' },
  USD: { locale: 'en-US', currency: 'USD' },
  EUR: { locale: 'de-DE', currency: 'EUR' },
  KZT: { locale: 'kk-KZ', currency: 'KZT' },
};

function getUserCurrency() {
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.currency || 'RUB';
    }
  } catch {}
  return 'RUB';
}

export function formatCurrency(amount, currencyOverride) {
  const code = currencyOverride || getUserCurrency();
  const config = CURRENCY_CONFIG[code] || CURRENCY_CONFIG.RUB;
  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: config.currency,
    minimumFractionDigits: 0,
  }).format(amount || 0);
}

export function generateId() {
  return crypto.randomUUID();
}

export const ORDER_STATUSES = {
  new: { label: 'Новый', color: 'bg-emerald-500' },
  in_progress: { label: 'В работе', color: 'bg-orange-500' },
  completed: { label: 'Завершён', color: 'bg-slate-800' },
  cancelled: { label: 'Отменён', color: 'bg-red-500' },
};

export const TASK_PRIORITIES = {
  low: { label: 'Низкий', color: 'text-emerald-600' },
  medium: { label: 'Средний', color: 'text-amber-600' },
  high: { label: 'Высокий', color: 'text-red-600' },
};

export const TASK_STATUSES = {
  pending: { label: 'Ожидает', color: 'bg-slate-200' },
  in_progress: { label: 'В работе', color: 'bg-amber-200' },
  completed: { label: 'Выполнено', color: 'bg-emerald-200' },
};

export const FIELD_TYPES = [
  { 
    type: 'text', 
    label: 'Текст (строка)', 
    shortLabel: 'Текст', 
    icon: 'Type',
    description: 'Однострочный текст для имён, номеров, адресов'
  },
  { 
    type: 'number', 
    label: 'Число', 
    shortLabel: 'Число', 
    icon: 'Hash',
    description: 'Цифры с возможностью задать минимум и максимум'
  },
  { 
    type: 'textarea', 
    label: 'Текст (абзац)', 
    shortLabel: 'Абзац', 
    icon: 'AlignLeft',
    description: 'Многострочный текст для комментариев и описаний'
  },
  { 
    type: 'select', 
    label: 'Выбор из списка', 
    shortLabel: 'Список', 
    icon: 'ChevronDown',
    description: 'Выпадающий список для выбора одного варианта (5+ вариантов)'
  },
  { 
    type: 'radio', 
    label: 'Выбор (видны все)', 
    shortLabel: 'Выбор', 
    icon: 'Circle',
    description: 'Радиокнопки для выбора одного варианта (2-5 вариантов, все видны)'
  },
  { 
    type: 'multi_select', 
    label: 'Выбор нескольких', 
    shortLabel: 'Несколько', 
    icon: 'ListChecks',
    description: 'Чекбоксы для выбора нескольких вариантов из списка'
  },
  { 
    type: 'checkbox', 
    label: 'Да/Нет (галочка)', 
    shortLabel: 'Да/Нет', 
    icon: 'CheckSquare',
    description: 'Одна галочка для согласия, подтверждения или включения опции'
  },
  { 
    type: 'date', 
    label: 'Дата', 
    shortLabel: 'Дата', 
    icon: 'Calendar',
    description: 'Выбор даты через календарь'
  },
  { 
    type: 'time', 
    label: 'Время', 
    shortLabel: 'Время', 
    icon: 'Clock',
    description: 'Выбор времени (часы и минуты)'
  },
  { 
    type: 'file', 
    label: 'Загрузка файла', 
    shortLabel: 'Файл', 
    icon: 'Paperclip',
    description: 'Загрузка документов, PDF, изображений'
  },
  { 
    type: 'image_gallery', 
    label: 'Галерея фото', 
    shortLabel: 'Фото', 
    icon: 'Images',
    description: 'Загрузка нескольких изображений с предпросмотром'
  },
  { 
    type: 'section', 
    label: 'Разделитель (заголовок)', 
    shortLabel: 'Раздел', 
    icon: 'LayoutList',
    description: 'Визуальный разделитель для группировки полей'
  },
];
